import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calculator, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { api } from "../lib/api";

const COUNTRIES = ["Malaysia", "Taiwan", "Jerman", "Belanda", "Korea Selatan", "Australia", "UK", "USA"];

export default function ROICalculator() {
    const [tuition, setTuition] = useState(80_000_000);
    const [living, setLiving] = useState(60_000_000);
    const [c1, setC1] = useState("Malaysia");
    const [c2, setC2] = useState("Jerman");
    const [result, setResult] = useState(null);

    useEffect(() => {
        const t = setTimeout(() => {
            api.post("/calculator/roi", {
                tuition_idr: tuition,
                living_idr: living,
                countries: [c1, c2],
            }).then((r) => setResult(r.data));
        }, 300);
        return () => clearTimeout(t);
    }, [tuition, living, c1, c2]);

    const chartData = result?.results?.map((r) => ({
        name: r.negara,
        biaya: Math.round(r.total_biaya_idr / 1_000_000),
        gaji: Math.round(r.gaji_rata2_tahunan_idr / 1_000_000),
    })) || [];

    return (
        <section id="roi" className="bg-white py-20 md:py-28" data-testid="roi-section">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="text-center mb-12 max-w-3xl mx-auto">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0A1A2F] text-white text-xs font-semibold mb-4">
                        <Calculator className="w-3.5 h-3.5 text-[#F5A623]" />
                        LANGKAH 2 · HITUNG REALISTIS
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1A2F] tracking-tight leading-tight">
                        Hitung Estimasi Biaya Kuliah & Balik Modal
                    </h2>
                    <p className="mt-4 text-base text-slate-600 leading-relaxed">
                        Bandingkan estimasi biaya kuliah, biaya hidup, dan potensi penghasilan setelah lulus. Simulasi ini membantu keluarga berdiskusi lebih realistis sebelum memilih negara.
                    </p>
                    <p className="mt-3 text-xs text-slate-500 italic">
                        *Simulasi ini bersifat estimasi awal, bukan jaminan penghasilan atau hasil karier.
                    </p>
                </div>

                <div className="grid lg:grid-cols-2 gap-8">
                    <div className="bg-[#F8FAFC] rounded-3xl border border-slate-200 p-8">
                        <h3 className="font-display font-semibold text-xl text-[#0A1A2F] mb-6">Atur Estimasi Biayamu</h3>

                        <SliderRow
                            label="Biaya Kuliah / Tahun"
                            value={tuition} min={20_000_000} max={800_000_000} step={5_000_000}
                            onChange={setTuition}
                            tid="slider-tuition"
                        />
                        <SliderRow
                            label="Biaya Hidup / Tahun"
                            value={living} min={20_000_000} max={400_000_000} step={5_000_000}
                            onChange={setLiving}
                            tid="slider-living"
                        />

                        <div className="grid grid-cols-2 gap-4 mt-6">
                            <Select label="Negara A" value={c1} onChange={setC1} tid="select-country-a" />
                            <Select label="Negara B" value={c2} onChange={setC2} tid="select-country-b" />
                        </div>
                    </div>

                    <div className="bg-[#0A1A2F] rounded-3xl p-8 text-white" data-testid="roi-result-panel">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F5A623] font-bold mb-4">
                            <TrendingUp className="w-4 h-4" /> Hasil Analisa
                        </div>
                        <div className="h-64 min-h-[16rem]">
                            <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                                <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#1e3358" />
                                    <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
                                    <YAxis stroke="#94a3b8" fontSize={11} unit=" jt" />
                                    <Tooltip
                                        contentStyle={{ background: "#0A1A2F", border: "1px solid #F5A623", borderRadius: "12px" }}
                                        formatter={(v) => `Rp ${v} jt`}
                                    />
                                    <Bar dataKey="biaya" fill="#F5A623" name="Total Biaya 2thn" radius={[8, 8, 0, 0]}>
                                        {chartData.map((d) => <Cell key={`cell-${d.name}`} fill="#F5A623" />)}
                                    </Bar>
                                    <Bar dataKey="gaji" fill="#60a5fa" name="Gaji Rata-rata/thn" radius={[8, 8, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {result?.results && (
                            <div className="grid grid-cols-2 gap-3 mt-6">
                                {result.results.map((r, i) => (
                                    <motion.div
                                        key={r.negara}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.1 }}
                                        className="bg-white/5 border border-white/10 rounded-2xl p-4"
                                        data-testid={`roi-card-${r.negara}`}
                                    >
                                        <div className="text-xs uppercase tracking-wider text-slate-400">{r.negara}</div>
                                        <div className="font-display text-2xl font-bold text-[#F5A623] mt-1">
                                            {r.estimasi_balik_modal_tahun} <span className="text-sm text-white/70">tahun</span>
                                        </div>
                                        <div className="text-[11px] text-slate-400 mt-1">Estimasi balik modal</div>
                                    </motion.div>
                                ))}
                            </div>
                        )}
                        <p className="text-xs text-slate-400 mt-5 italic">
                            *Estimasi awal berdasarkan rata-rata gaji lulusan dan 60% disposable income. Bukan jaminan penghasilan. Diskusikan hasil ini dengan Counselor Gantari untuk simulasi yang lebih sesuai profilmu.
                        </p>
                        <a
                            href="https://wa.me/6285195555610?text=Halo%20Gantari%20Edu%2C%20saya%20ingin%20diskusi%20hasil%20simulasi%20biaya%20dan%20ROI%20kuliah%20luar%20negeri."
                            target="_blank"
                            rel="noopener noreferrer"
                            data-cta="cta_roi_whatsapp"
                            data-testid="roi-cta-whatsapp"
                            className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#F5A623] text-[#0A1A2F] hover:bg-[#D98F1B] rounded-full py-3 font-bold text-sm transition-all"
                        >
                            Bahas Simulasi Ini dengan Counselor
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}

function SliderRow({ label, value, min, max, step, onChange, tid }) {
    return (
        <div className="mb-6">
            <div className="flex justify-between items-baseline mb-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
                <span className="font-display font-bold text-lg text-[#0A1A2F]">
                    Rp {(value / 1_000_000).toFixed(0)} jt
                </span>
            </div>
            <input
                type="range" min={min} max={max} step={step} value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                data-testid={tid}
                className="w-full accent-[#F5A623]"
            />
            <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                <span>Rp {min / 1_000_000} jt</span>
                <span>Rp {max / 1_000_000} jt</span>
            </div>
        </div>
    );
}

function Select({ label, value, onChange, tid }) {
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{label}</label>
            <select
                value={value} onChange={(e) => onChange(e.target.value)}
                data-testid={tid}
                className="w-full border-2 border-slate-200 bg-white rounded-xl px-4 py-3 focus:border-[#0A1A2F] outline-none"
            >
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
        </div>
    );
}
